import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getDriverAvailabilityTokenByToken,
  getDriver,
  saveDriverAvailabilityResponse,
  markDriverAvailabilityTokenUsed,
} from '../services/firebase';
import { VEHICLE_MAKES_MODELS, VEHICLE_COLORS } from '../data/vehicleData';

const DAYS = [
  { key: 'thu', label: 'Thursday, April 9',   field: 'thuAvailability' },
  { key: 'fri', label: 'Friday, April 10',    field: 'friAvailability' },
  { key: 'sat', label: 'Saturday, April 11',  field: 'satAvailability' },
  { key: 'sun', label: 'Sunday, April 12',    field: 'sunAvailability' },
];

// Individual time windows a driver can select (multi-select)
const DRIVER_SHIFT_TIMES = ['6-10 AM', '10 AM-1 PM', '1-4 PM', '4-8 PM'];

function toArray(val) {
  if (!val || val === '' || val === 'Not Available') return [];
  if (val === 'Anytime') return [...DRIVER_SHIFT_TIMES];
  if (Array.isArray(val)) return val;
  return [];
}

function getMode(val) {
  if (!val || val === '') return 'blank';
  if (val === 'Not Available') return 'unavailable';
  if (val === 'Anytime') return 'anytime';
  return 'specific';
}

function DriverAvailabilityResponse() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading | invalid | form | submitted
  const [tokenDoc, setTokenDoc] = useState(null);
  const [driver, setDriver] = useState(null);

  const [availability, setAvailability] = useState({
    thuAvailability: '',
    friAvailability: '',
    satAvailability: '',
    sunAvailability: '',
  });

  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleColor, setVehicleColor] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [passengerCapacity, setPassengerCapacity] = useState('');
  const [cartCapacity, setCartCapacity] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = searchParams.get('t');
    if (!token) { setStatus('invalid'); return; }

    (async () => {
      const td = await getDriverAvailabilityTokenByToken(token);
      if (!td || td.used) { setStatus('invalid'); return; }

      const drv = await getDriver(td.driverId);
      if (!drv) { setStatus('invalid'); return; }

      setTokenDoc(td);
      setDriver(drv);

      setAvailability({
        thuAvailability: drv.thuAvailability || '',
        friAvailability: drv.friAvailability || '',
        satAvailability: drv.satAvailability || '',
        sunAvailability: drv.sunAvailability || '',
      });
      setVehicleMake(drv.vehicleMake || '');
      setVehicleModel(drv.vehicleModel || '');
      setVehicleColor(drv.vehicleColor || '');
      setVehicleType(drv.vehicleType || '');
      setPassengerCapacity(drv.passengerCapacity != null ? String(drv.passengerCapacity) : '');
      setCartCapacity(drv.cartCapacity != null ? String(drv.cartCapacity) : '');

      setStatus('form');
    })();
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await saveDriverAvailabilityResponse(tokenDoc.driverId, {
      ...availability,
      vehicleMake,
      vehicleModel,
      vehicleColor,
      vehicleType,
      passengerCapacity: passengerCapacity !== '' ? Number(passengerCapacity) : null,
      cartCapacity: cartCapacity !== '' ? Number(cartCapacity) : null,
    });
    await markDriverAvailabilityTokenUsed(tokenDoc.token);
    setStatus('submitted');
    setSubmitting(false);
  };

  const header = (
    <div style={{ background: '#1e3a5f', color: 'white', padding: '20px 28px', borderRadius: '8px 8px 0 0' }}>
      <h2 style={{ margin: 0, fontSize: '20px' }}>SMPW Masters Event 2026 — Shuttling / Transportation</h2>
      <p style={{ margin: '4px 0 0', opacity: 0.75, fontSize: '14px' }}>Augusta, Georgia — April 9–12, 2026</p>
    </div>
  );

  if (status === 'loading') return <div className="avail-response-page">{header}<div style={{ border: '1px solid #e2e8f0', borderTop: 'none', padding: '28px', borderRadius: '0 0 8px 8px' }}><p>Loading…</p></div></div>;

  if (status === 'invalid') return (
    <div className="avail-response-page">
      {header}
      <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', padding: '28px', borderRadius: '0 0 8px 8px' }}>
        <p style={{ color: '#dc2626', fontWeight: 600 }}>This link is invalid or has already been used.</p>
        <p style={{ color: '#64748b', fontSize: '14px' }}>If you believe this is an error, please contact your scheduling coordinator.</p>
      </div>
    </div>
  );

  if (status === 'submitted') return (
    <div className="avail-response-page">
      {header}
      <div className="avail-thankyou" style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
        <h3 style={{ color: '#166534', marginBottom: '8px' }}>Thank You, {driver?.firstName}!</h3>
        <p style={{ color: '#15803d' }}>Your transportation availability and vehicle information have been submitted. We'll be in touch with your shuttle assignment details.</p>
      </div>
    </div>
  );

  return (
    <div className="avail-response-page">
      {header}
      <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', padding: '28px', borderRadius: '0 0 8px 8px' }}>
        <h3 style={{ color: '#1e3a5f', marginTop: 0 }}>Driver / Shuttler Information</h3>
        <p>Hi {driver?.firstName}, please confirm your vehicle details and availability for each day of the 2026 Augusta Masters event.</p>

        <form onSubmit={handleSubmit}>
          {/* Vehicle Info */}
          <div style={{ background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '18px', marginBottom: '24px' }}>
            <h4 style={{ margin: '0 0 14px', color: '#1e40af', fontSize: '15px' }}>🚐 Vehicle Information</h4>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '14px', color: '#374151' }}>Make</label>
                <select
                  value={vehicleMake}
                  onChange={e => { setVehicleMake(e.target.value); setVehicleModel(''); }}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                >
                  <option value="">Select make…</option>
                  {[...Object.keys(VEHICLE_MAKES_MODELS).filter(m => m !== 'Other').sort(), 'Other'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '14px', color: '#374151' }}>Model</label>
                <select
                  value={vehicleModel}
                  onChange={e => setVehicleModel(e.target.value)}
                  disabled={!vehicleMake}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                >
                  <option value="">{vehicleMake ? 'Select model…' : 'Select make first'}</option>
                  {[...(VEHICLE_MAKES_MODELS[vehicleMake] || [])].sort().map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '14px', color: '#374151' }}>Color</label>
                <select
                  value={vehicleColor}
                  onChange={e => setVehicleColor(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                >
                  <option value="">Select color…</option>
                  {[...VEHICLE_COLORS.filter(c => c !== 'Other').sort(), 'Other'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '14px', color: '#374151' }}>Vehicle Type</label>
              <select
                value={vehicleType}
                onChange={e => setVehicleType(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
              >
                <option value="">Select type…</option>
                <option value="SUV">SUV</option>
                <option value="Minivan">Minivan</option>
                <option value="Van">Van</option>
                <option value="Sedan">Sedan</option>
                <option value="Hatchback">Hatchback</option>
                <option value="Truck / Pickup">Truck / Pickup</option>
                <option value="Crossover">Crossover</option>
                <option value="Wagon">Wagon</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', alignItems: 'end' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '14px', color: '#374151' }}>
                  Passenger Capacity <small style={{ fontWeight: 400, color: '#6b7280' }}>(not including driver)</small>
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={passengerCapacity}
                  onChange={e => setPassengerCapacity(e.target.value)}
                  placeholder="e.g. 4"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '14px', color: '#374151' }}>
                  Cart Capacity <small style={{ fontWeight: 400, color: '#6b7280' }}>(carts that fit)</small>
                </label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={cartCapacity}
                  onChange={e => setCartCapacity(e.target.value)}
                  placeholder="e.g. 2"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          </div>

          {/* Per-day availability */}
          <h4 style={{ margin: '0 0 14px', color: '#374151', fontSize: '15px' }}>📅 Availability by Day</h4>
          <p style={{ fontSize: '13px', color: '#64748b', marginTop: 0, marginBottom: '16px' }}>
            For each day, choose "Not Available" if you can't drive, "Anytime" if you're free all day, or select the specific windows that work for you — you can pick more than one.
          </p>

          {DAYS.map(day => {
            const mode = getMode(availability[day.field]);
            const selected = toArray(availability[day.field]);

            const setMode = (m) => {
              setAvailability(prev => ({
                ...prev,
                [day.field]: m === 'unavailable' ? 'Not Available'
                           : m === 'anytime'     ? 'Anytime'
                           : [],
              }));
            };

            const toggleShift = (shift) => {
              setAvailability(prev => {
                const current = toArray(prev[day.field]);
                const updated = current.includes(shift)
                  ? current.filter(s => s !== shift)
                  : [...current, shift];
                return { ...prev, [day.field]: updated };
              });
            };

            return (
              <div className="avail-day-row" key={day.key}>
                <div className="avail-day-label">{day.label}</div>

                <div className="avail-quick-row">
                  <button
                    type="button"
                    className={`avail-quick-btn${mode === 'unavailable' ? ' active-unavail' : ''}`}
                    onClick={() => setMode('unavailable')}
                  >
                    Not Available
                  </button>
                  <button
                    type="button"
                    className={`avail-quick-btn${mode === 'anytime' ? ' active-avail' : ''}`}
                    onClick={() => setMode('anytime')}
                  >
                    Anytime
                  </button>
                  <button
                    type="button"
                    className={`avail-quick-btn${mode === 'specific' ? ' active-specific' : ''}`}
                    onClick={() => setMode('specific')}
                  >
                    Select windows…
                  </button>
                </div>

                {(mode === 'specific' || mode === 'anytime') && (
                  <div className="avail-checkbox-grid">
                    {DRIVER_SHIFT_TIMES.map(shift => (
                      <label key={shift} className="avail-check-item">
                        <input
                          type="checkbox"
                          checked={selected.includes(shift)}
                          onChange={() => {
                            if (mode === 'anytime') {
                              // Switch to specific, deselect just this one
                              setAvailability(prev => ({
                                ...prev,
                                [day.field]: DRIVER_SHIFT_TIMES.filter(s => s !== shift),
                              }));
                            } else {
                              toggleShift(shift);
                            }
                          }}
                        />
                        {shift}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <button type="submit" className="avail-submit-btn" disabled={submitting} style={{ background: '#1e3a5f' }}>
            {submitting ? 'Submitting…' : 'Submit My Information'}
          </button>
        </form>

        <p style={{ marginTop: '20px', color: '#64748b', fontSize: '12px' }}>
          SMPW Scheduling Team – Augusta Masters
        </p>
      </div>
    </div>
  );
}

export default DriverAvailabilityResponse;
